import React, { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import Tabs, { TabList, TabPanel, useTab } from '@atlaskit/tabs';
import { Box, Stack, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import {
	AI_AUDIO_BRIEFING,
	AI_AUDIO_NARRATION,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import { useAudioStream } from '../../hooks/useAudioStream';
import { useAudioEventListener } from '../../hooks/useAudioEventListener';
import { useAudioBriefing } from '../../hooks/useAudioBriefing';
import { getSharedAttributes } from '../../utils/getSharedAttributes';
import {
	getAudioLengthMs,
	getAudioCurrentTimeMs,
	getAudioProgress,
} from '../../utils/audioTimeUtils';
import { AudioFeature } from '../../types/audioFeatureType';

import { AudioBriefingChangeboarding } from './AudioBriefingChangeboarding';
import { AudioBriefingCustomizePanel } from './panels/AudioBriefingCustomizePanel';
import { AudioBriefingSourcesPanel } from './panels/AudioBriefingSourcesPanel';
import { AudioBriefingPanel } from './panels/AudioBriefingPanel';
import { AudioNarrationPanel } from './panels/AudioNarrationPanel';
import { AudioBriefingErrorComponent } from './AudioBriefingErrorComponent';
import { AudioPopupHeader } from './AudioPopupHeader';
import { AI_AUDIO_BRIEFING_INTERACTION_METRIC } from './perf.config';
import { AudioPopupFooter } from './AudioPopupFooter';
import { MinimizedAudioPlayer } from './MinimizedAudioPlayer';

const i18n = defineMessages({
	briefingTab: {
		id: 'audio.audio-popup.briefing-tab',
		defaultMessage: 'Briefing',
		description: 'The name of the tab in the audio popup that directs towards the briefing panel',
	},
	narrationTab: {
		id: 'audio.audio-popup.narration-tab',
		defaultMessage: 'Full page',
		description: 'The name of the tab in the audio popup that directs towards the narration panel',
	},
	closeButtonLabel: {
		id: 'audio.audio-popup.close-label',
		defaultMessage: 'Close',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will close the audio popup',
	},
	briefingPrefetchLabel: {
		id: 'audio.audio-popup.briefing-prefetch-label',
		defaultMessage: 'High-level overview',
		description: 'The label for the prefetch state in the audio popup time indicator',
	},
	briefingLoadingLabel: {
		id: 'audio.audio-popup.briefing-loading-label',
		defaultMessage: 'Preparing briefing...',
		description: 'The label for the loading state in the audio popup time indicator',
	},
	narrationPrefetchLabel: {
		id: 'audio.audio-popup.narration-prefetch-label',
		defaultMessage: 'Read page as is',
		description:
			'The label for the prefetch state in the audio popup time indicator for the narration panel',
	},
	narrationLoadingLabel: {
		id: 'audio.audio-popup.narration-loading-label',
		defaultMessage: 'Preparing narration...',
		description:
			'The label for the loading state in the audio popup time indicator for the narration panel',
	},
});

type AudioPopupContentProps = {
	closePopup: (additionalAttributes?: Record<string, any>) => void;
	isPopupMinimized: boolean;
	setIsPopupMinimized: (isMinimized: boolean) => void;
	showChangeboarding: boolean;
	handleCloseChangeboarding: () => void;
};

type AudioPopupContentComponentProps = AudioPopupContentProps & {
	audioRef: React.RefObject<HTMLAudioElement>;
};

const tabsListStyles = css({
	marginBottom: token('space.150'),
	marginTop: token('space.150'),
	padding: token('space.050'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[role=tablist]': {
		marginInlineStart: 'unset',
		gap: token('space.050'),
		display: 'flex',
		width: '100%',
		'&::before': {
			all: 'unset',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[role=tab]': {
			padding: 'unset',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&[aria-selected=true]::after': {
				insetInlineStart: 'unset',
			},
			'&:hover::after': {
				insetInlineStart: 'unset',
			},
		},
	},
});

const styles = cssMap({
	// These styles are used to style the popup to be in parity with Atlaskit's default popup styles
	popupStyles: {
		boxSizing: 'border-box',
		zIndex: 400,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: '1rem',
		boxShadow: token('elevation.shadow.overlay'),
	},
	wrapper: {
		width: '360px',
	},
	headerTitle: {
		flex: 1,
	},
	customTab: {
		borderRadius: token('radius.small'),
		flex: 1,
		backgroundColor: token('elevation.surface'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body.small'),
		height: '24px',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	selectedTab: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
});

const CustomTab = ({ label }: { label: string }) => {
	const tabAttributes = useTab();
	return (
		<Pressable
			{...tabAttributes}
			xcss={cx(styles.customTab, tabAttributes['aria-selected'] && styles.selectedTab)}
		>
			<Inline alignInline="center" alignBlock="center" space="space.050">
				<Box>{label}</Box>
			</Inline>
		</Pressable>
	);
};

export const AudioPopupContentComponent = ({
	closePopup,
	audioRef,
	showChangeboarding,
	handleCloseChangeboarding,
	isPopupMinimized,
	setIsPopupMinimized,
}: AudioPopupContentComponentProps) => {
	const { formatMessage } = useIntl();
	const [isCustomizePanelOpen, setIsCustomizePanelOpen] = useState(false);
	const [isSourcesPanelOpen, setIsSourcesPanelOpen] = useState(false);
	const [selectedTab, setSelectedTab] = useState(0);

	const [
		{
			activeFeature,
			activeContent,
			additionalResources,
			customization,
			error,
			isLoading,
			streamEnded,
			hasFetched,
			lastGeneratedBriefingConfig,
			playbackSpeed,
		},
		{ fetchBriefing, fetchNarration },
	] = useAudioBriefing();

	const { trackAIResultView, trackAIInteractionInit, trackAIResultAction } =
		useAIEventsInstrumentation();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sharedAttributes = getSharedAttributes({
		customization: lastGeneratedBriefingConfig?.customization,
		additionalResources: lastGeneratedBriefingConfig?.additionalResources,
		playbackSpeed,
		hasFetched,
		activeFeature,
	});

	const sendAnalyticsUIEvent = useCallback(
		(actionSubjectId: string, additionalAttributes?: Record<string, string | boolean>) => {
			const attributes = {
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'audioBriefingPopup',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	// Fire experience tracking success when the audio finishes streaming
	useEffect(() => {
		const fetchSuccess = hasFetched && !error && streamEnded;

		const feature =
			activeFeature === AudioFeature.BRIEFING ? AI_AUDIO_BRIEFING : AI_AUDIO_NARRATION;

		if (fetchSuccess) {
			experienceTracker.succeed({
				name: feature,
			});
		}
	}, [hasFetched, error, streamEnded, experienceTracker, activeFeature]);

	// Fire AI success when the audio starts playing
	useEffect(() => {
		if (hasFetched && !isLoading && !error) {
			trackAIResultView({
				attributes: getSharedAttributes({
					customization: lastGeneratedBriefingConfig?.customization,
					additionalResources: lastGeneratedBriefingConfig?.additionalResources,
					playbackSpeed,
					hasFetched,
					activeFeature,
				}),
			});
			AI_AUDIO_BRIEFING_INTERACTION_METRIC.stop();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasFetched, isLoading, error]);

	const handleFetchBriefing = () => {
		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		AI_AUDIO_BRIEFING_INTERACTION_METRIC.start();
		fetchBriefing();

		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature: AudioFeature.BRIEFING,
			}),
		});
	};

	const handleFetchNarration = () => {
		experienceTracker.start({
			name: AI_AUDIO_NARRATION,
			attributes: {
				contentId: activeContent?.id,
			},
		});

		fetchNarration();
		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature: AudioFeature.NARRATION,
			}),
		});
	};

	const AUDIO_TABS = [
		{
			key: AudioFeature.BRIEFING,
			label: i18n.briefingTab,
			prefetchLabel: i18n.briefingPrefetchLabel,
			loadingLabel: i18n.briefingLoadingLabel,
			fetchContent: handleFetchBriefing,
		},
		{
			key: AudioFeature.NARRATION,
			label: i18n.narrationTab,
			prefetchLabel: i18n.narrationPrefetchLabel,
			loadingLabel: i18n.narrationLoadingLabel,
			fetchContent: handleFetchNarration,
		},
	];

	if (error) {
		const handleTryAgain =
			activeFeature === AudioFeature.BRIEFING ? handleFetchBriefing : handleFetchNarration;
		return (
			<AudioBriefingErrorComponent
				error={error}
				audioRef={audioRef}
				closePopup={closePopup}
				onTryAgain={handleTryAgain}
			/>
		);
	}

	if (showChangeboarding) {
		return (
			<AudioBriefingChangeboarding
				closePopup={closePopup}
				handleFetchContent={handleFetchBriefing}
				onCloseChangeboarding={handleCloseChangeboarding}
			/>
		);
	}

	if (isCustomizePanelOpen) {
		return (
			<Box xcss={styles.wrapper}>
				<AudioBriefingCustomizePanel
					onBack={() => setIsCustomizePanelOpen(false)}
					audioRef={audioRef}
				/>
			</Box>
		);
	}

	if (isSourcesPanelOpen) {
		return (
			<Box xcss={styles.wrapper}>
				<AudioBriefingSourcesPanel
					onBack={() => setIsSourcesPanelOpen(false)}
					audioRef={audioRef}
				/>
			</Box>
		);
	}

	const handleClose = () => {
		if (hasFetched && !isLoading && !error) {
			const audioClosedAttributes = {
				audioLengthMs: getAudioLengthMs(audioRef),
				audioCurrentTimeMs: getAudioCurrentTimeMs(audioRef),
				percentCompleted: getAudioProgress(audioRef),
				reason: 'closed from popup controls',
			};
			closePopup({
				...audioClosedAttributes,
			});
		} else {
			closePopup();
		}
	};

	const handleExpand = () => {
		trackAIResultAction('audioExpandPopup', {
			attributes: sharedAttributes,
		});

		if (hasFetched) {
			const activeFeatureTabIndex = AUDIO_TABS.findIndex((tab) => tab.key === activeFeature);

			if (activeFeatureTabIndex !== -1) {
				setSelectedTab(activeFeatureTabIndex);
			}
		}

		setIsPopupMinimized(false);
	};

	const handleMinimize = () => {
		trackAIResultAction('audioMinimizePopup', {
			attributes: sharedAttributes,
		});
		setIsPopupMinimized(true);
	};

	if (isPopupMinimized) {
		const activeTabData = AUDIO_TABS[selectedTab];
		return (
			<MinimizedAudioPlayer
				audioRef={audioRef}
				onClose={handleClose}
				onMaximize={handleExpand}
				fetchContent={activeTabData.fetchContent}
				prefetchLabel={formatMessage(activeTabData.prefetchLabel)}
				loadingLabel={formatMessage(activeTabData.loadingLabel)}
			/>
		);
	}

	const handleTabChange = (index: number) => {
		const tabName = AUDIO_TABS[index].key;
		setSelectedTab(index);

		sendAnalyticsUIEvent('audioSelectTab', {
			tab: tabName,
			hasFetched,
		});
	};

	const handleCustomizeClick = () => {
		sendAnalyticsUIEvent('audioCustomize', {
			hasFetched,
		});
		setIsCustomizePanelOpen(true);
	};

	const handleSourcesClick = () => {
		sendAnalyticsUIEvent('audioSources', {
			hasFetched,
		});
		setIsSourcesPanelOpen(true);
	};

	return (
		<Box xcss={styles.wrapper}>
			<Stack>
				<Box padding="space.200">
					<AudioPopupHeader
						contentId={activeContent?.id ?? ''}
						contentType={activeContent?.contentType ?? ''}
						onClose={handleClose}
						onMinimize={handleMinimize}
					/>
					<Tabs id="audio-tabs" onChange={handleTabChange} selected={selectedTab}>
						<div css={tabsListStyles}>
							<TabList>
								{AUDIO_TABS.map((tab) => (
									<CustomTab key={tab.key} label={formatMessage(tab.label)} />
								))}
							</TabList>
						</div>
						<TabPanel>
							<AudioBriefingPanel
								audioRef={audioRef}
								handleFetchContent={handleFetchBriefing}
								onCustomizeClick={handleCustomizeClick}
								onSourcesClick={handleSourcesClick}
								prefetchLabel={formatMessage(i18n.briefingPrefetchLabel)}
								loadingLabel={formatMessage(i18n.briefingLoadingLabel)}
							/>
						</TabPanel>
						<TabPanel>
							<AudioNarrationPanel
								audioRef={audioRef}
								handleFetchContent={handleFetchNarration}
								prefetchLabel={formatMessage(i18n.narrationPrefetchLabel)}
								loadingLabel={formatMessage(i18n.narrationLoadingLabel)}
							/>
						</TabPanel>
					</Tabs>
				</Box>
				<AudioPopupFooter activeContent={activeContent} activeFeature={activeFeature} />
			</Stack>
		</Box>
	);
};

export const AudioPopupContent = (passThroughProps: AudioPopupContentProps): JSX.Element => {
	const [
		{
			stream,
			playbackSpeed,
			isLoading,
			customization,
			additionalResources,
			hasFetched,
			activeFeature,
		},
		{ pauseContent, resumeContent, endContent, setContentError, setStreamEnded },
	] = useAudioBriefing();
	const { audioRef } = useAudioStream(stream, setContentError, setStreamEnded);

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const autoplayContent = async () => {
		if (!audioRef.current) {
			return;
		}

		await audioRef.current.play();
	};

	const fireContentBufferEvent = useCallback(() => {
		trackAIResultAction('audioContentBuffer', {
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature,
			}),
		});
	}, [
		trackAIResultAction,
		customization,
		additionalResources,
		playbackSpeed,
		hasFetched,
		activeFeature,
	]);

	useAudioEventListener({
		audioRef,
		onContentPaused: pauseContent,
		onContentResumed: resumeContent,
		onContentEnded: endContent,
		onContentCanPlay: autoplayContent,
		onContentBuffer: fireContentBufferEvent,
	});

	// TODO: Remove this useEffect once autoplay logic is refactored
	// https://hello.jira.atlassian.cloud/browse/CCI-4396
	useEffect(() => {
		const audio = audioRef.current;
		if (audio && !isLoading) {
			audio.playbackRate = playbackSpeed;
		}
	}, [playbackSpeed, audioRef, isLoading]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'audioBriefingPopup',
			},
		}).fire();
		// This is only needed for the first render of the popup
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box xcss={styles.popupStyles}>
			{/* eslint-disable-next-line @atlassian/a11y/media-has-caption -- The summary is already available in text so the track isn't very necessary */}
			<audio data-testid="popup-audio-element" ref={audioRef} />
			<AudioPopupContentComponent audioRef={audioRef} {...passThroughProps} />
		</Box>
	);
};
